import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Save, Send } from "lucide-react";
import { toast } from "sonner";
import { Field, SectionCard } from "./shared";
import LineGrid, { blankGridRow, formatCurrency, gridRowsToLines } from "./LineGrid";
import { encodeVendorSelection } from "./AccountSelect";
import VendorSelect from "./VendorSelect";
import { ACCOUNTING_BASE } from "./accountingNav";

/**
 * Shared multi-line voucher entry page for the "advanced" voucher types -
 * Journal, Purchase / Vendor Bill, Credit Note, Debit Note - all of which
 * need real multi-line debit/credit support rather than the fixed two-field
 * shape the old single-debit/single-credit screens assumed. Posts through
 * the same central engine (/engine/vouchers) as every other voucher type.
 */
export default function GridVoucherFormPage({ voucherType, title, subtitle, referenceLabel = "Reference", emptyStateNote, showVendorHelper = false, immediateSave = false, restrictBankCash = false, showDueDate = false, showAttachment = false }) {
  const [rows, setRows] = useState([blankGridRow(), blankGridRow()]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [narration, setNarration] = useState("");
  const [vendorHelper, setVendorHelper] = useState("");
  // Phase 15 (Vendor Bills & Payables) item 1: reuses the existing generic
  // Society attachment system (society_attachments, entityType/entityId) -
  // never a new attachment mechanism. A voucher has no id until it is
  // actually created, so the file itself is held here and uploaded right
  // after save succeeds, against the newly-created voucher's id.
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  // Phase 13 (Journal/Contra correction), items 1+2: Journal's ledger
  // selector defaults to excluding Bank/Cash entirely (a non-cash
  // adjustment screen has no business offering them, and Receipt/Payment/
  // Contra are the correct place for real bank/cash movements) - this
  // opt-in checkbox is the explicit escape hatch the correction allows
  // ("if Bank/Cash must be available for a genuine adjustment"). When on,
  // it falls back to the exact same deduped Bank Account/Cash pickers every
  // other voucher screen already uses - never a raw duplicate ledger.
  const [allowBankCash, setAllowBankCash] = useState(false);
  const journalHideCash = restrictBankCash && !allowBankCash;
  const journalHideBankAccounts = restrictBankCash && !allowBankCash;
  const journalContext = restrictBankCash ? (allowBankCash ? "journalAdjustmentWithBankCash" : "journalAdjustment") : undefined;

  const load = async () => {
    try {
      const res = await api.get(`/society/accounting/engine/vouchers?voucherType=${voucherType}`).catch(() => ({ data: [] }));
      setVouchers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load vouchers"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const postExistingDraft = async (v) => {
    try {
      await api.post(`/society/accounting/engine/vouchers/${v.id}/post`);
      toast.success(`${title} posted`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not post this voucher"));
    }
  };

  const deleteExistingDraft = async (v) => {
    if (!window.confirm(`Delete this draft ${title.toLowerCase()}? This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${v.id}`);
      toast.success("Draft deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this draft"));
    }
  };

  // Phase 13 (Contra & Journal Voucher), item 4: a genuine hard delete for
  // an already-posted voucher, distinct from Cancel (which keeps full
  // history via an auto-reversal) - mirrors BankVoucherFormPage.jsx's own
  // deletePostedVoucher. Only reachable when immediateSave is true (Journal
  // in this pass) - Purchase / Credit Note / Debit Note keep their existing
  // Draft/Post/Cancel-only behavior untouched, per the explicit "do not
  // implement ... Vendor Bills" scope limit. The backend
  // (engine_delete_voucher) decides safety and reports the exact reason if
  // something depends on this voucher.
  const deletePostedVoucher = async (v) => {
    if (!window.confirm(`Permanently delete ${v.voucherNumber || "this voucher"}? Its accounting entries are removed completely and immediately - unlike Cancel, no reversal record is kept. This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${v.id}`);
      toast.success("Voucher permanently deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this voucher"));
    }
  };

  const cancelPostedVoucher = async (v) => {
    const reason = window.prompt(`Cancel ${v.voucherNumber}? This posts an automatic reversal and cannot be undone. Cancellation reason (required):`, "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A cancellation reason is required"); return; }
    try {
      await api.post(`/society/accounting/engine/vouchers/${v.id}/cancel`, { reason: reason.trim() });
      toast.success("Voucher cancelled and reversed");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not cancel this voucher"));
    }
  };

  const lines = gridRowsToLines(rows);
  const totalDebit = lines.reduce((s, l) => s + l.debit, 0);
  const totalCredit = lines.reduce((s, l) => s + l.credit, 0);
  const balanced = lines.length >= 2 && Math.round((totalDebit - totalCredit) * 100) / 100 === 0 && totalDebit > 0;
  const shortTitle = title.replace(/\s*Voucher$/i, "");

  const resetForm = () => {
    setRows([blankGridRow(), blankGridRow()]);
    setReferenceNo("");
    setNarration("");
    setDueDate("");
    setAttachmentFile(null);
  };

  // Phase 15 (Vendor Bills & Payables) item 1: uploads the pending
  // attachment (if any) against the just-created voucher's own id, via the
  // existing generic Society attachment system - never blocks/fails the
  // voucher save itself if the upload has a problem.
  const uploadPendingAttachment = async (voucherId) => {
    if (!attachmentFile) return;
    try {
      const formData = new FormData();
      formData.append("entityType", "voucher");
      formData.append("entityId", voucherId);
      formData.append("label", attachmentFile.name || "Vendor Bill attachment");
      formData.append("file", attachmentFile);
      await api.post("/society/attachments", formData);
    } catch (err) {
      toast.error(formatErr(err, "Voucher saved, but the attachment could not be uploaded"));
    }
  };

  // A Purchase / Vendor Bill is always identified by the vendor's own
  // invoice number (never an auto-generated society sequence), so it is
  // required here client-side and enforced again server-side.
  const referenceRequired = voucherType === "PURCHASE";

  const saveDraft = async () => {
    if (lines.length < 2) { toast.error("Add at least two lines"); return; }
    if (referenceRequired && !referenceNo.trim()) { toast.error(`${referenceLabel} is required for a ${shortTitle}`); return; }
    try {
      setBusy(true);
      const draft = await api.post("/society/accounting/engine/vouchers", { voucherType, voucherDate: date, dueDate, narration, referenceNo, lines });
      await uploadPendingAttachment(draft.data.id);
      toast.success(`${title} saved as draft. Draft is not posted to the books until you Post it.`);
      resetForm();
      await load();
    } catch (err) {
      toast.error(formatErr(err, `Could not save ${title.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };

  const saveAndPost = async () => {
    if (!balanced) { toast.error("Total Debit must equal Total Credit before posting"); return; }
    if (referenceRequired && !referenceNo.trim()) { toast.error(`${referenceLabel} is required for a ${shortTitle}`); return; }
    try {
      setBusy(true);
      const draft = await api.post("/society/accounting/engine/vouchers", { voucherType, voucherDate: date, dueDate, narration, referenceNo, lines });
      await api.post(`/society/accounting/engine/vouchers/${draft.data.id}/post`);
      await uploadPendingAttachment(draft.data.id);
      toast.success(`${title} posted`);
      resetForm();
      await load();
    } catch (err) {
      toast.error(formatErr(err, `Could not post ${title.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };

  // Phase 13 (Contra & Journal Voucher), item 3: "No mandatory Draft/Post
  // workflow. SAVE = accounting entry immediately." - Journal's own single-
  // action Save, mirroring Receipt/Contra's saveReceipt/saveContra on the
  // bank-aware screens. Only used when immediateSave is true; Purchase /
  // Credit Note / Debit Note (the other GridVoucherFormPage consumers) keep
  // calling saveDraft/saveAndPost exactly as before - this is deliberately
  // additive, not a replacement, so those completed modules are untouched.
  const saveImmediate = async () => {
    if (!balanced) { toast.error("Total Debit must equal Total Credit before saving"); return; }
    if (referenceRequired && !referenceNo.trim()) { toast.error(`${referenceLabel} is required for a ${shortTitle}`); return; }
    try {
      setBusy(true);
      const draft = await api.post("/society/accounting/engine/vouchers", { voucherType, voucherDate: date, narration, referenceNo, lines });
      await api.post(`/society/accounting/engine/vouchers/${draft.data.id}/post`);
      toast.success(`${title} saved`);
      resetForm();
      await load();
    } catch (err) {
      toast.error(formatErr(err, `Could not save ${title.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-[#6B7280]">{subtitle}</p>}
      </div>

      <SectionCard title="Voucher entry">
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label={referenceRequired ? `${referenceLabel} *` : referenceLabel}>
            <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} required={referenceRequired} placeholder={referenceRequired ? "e.g. INV-4521 (as printed on the vendor's bill)" : undefined} />
          </Field>
          {showDueDate && (
            <Field label="Due Date">
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} min={date || undefined} />
            </Field>
          )}
          {showVendorHelper && (
            <Field label="Vendor (adds a Vendor Payable line below)">
              <div className="flex gap-2">
                <div className="flex-1"><VendorSelect value={vendorHelper} onChange={setVendorHelper} /></div>
                <Button
                  type="button" variant="outline"
                  disabled={!vendorHelper}
                  onClick={() => {
                    setRows((r) => [...r, { ...blankGridRow(), selection: encodeVendorSelection(vendorHelper) }]);
                    setVendorHelper("");
                  }}
                >
                  Add
                </Button>
              </div>
            </Field>
          )}
          <div className="md:col-span-3">
            <Field label="Narration"><Textarea rows={2} value={narration} onChange={(e) => setNarration(e.target.value)} /></Field>
          </div>
          {showAttachment && (
            <div className="md:col-span-3">
              <Field label="Attachment (optional - vendor invoice copy, receipt, etc.)">
                <input
                  type="file" accept="image/*,application/pdf"
                  onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-[#374151] file:mr-3 file:rounded-sm file:border-0 file:bg-[#F3F4F6] file:px-3 file:py-1.5 file:text-xs file:font-semibold"
                />
              </Field>
              {attachmentFile && <p className="mt-1 text-xs text-[#6B7280]">{attachmentFile.name} - will be attached once this {shortTitle.toLowerCase()} is saved.</p>}
            </div>
          )}
        </div>

        {restrictBankCash && (
          <label className="mt-3 flex items-center gap-2 text-xs text-[#6B7280]">
            <input type="checkbox" checked={allowBankCash} onChange={(e) => setAllowBankCash(e.target.checked)} className="h-3.5 w-3.5" />
            Include Bank/Cash ledgers below (for a genuine adjustment only - prefer Receipt, Payment or Contra for ordinary bank/cash transactions)
          </label>
        )}

        <div className="mt-4">
          <LineGrid rows={rows} onChange={setRows} hideCash={journalHideCash} hideBankAccounts={journalHideBankAccounts} context={journalContext} />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {immediateSave ? (
            <Button type="button" disabled={busy || !balanced} onClick={saveImmediate} className="bg-[#111827] text-white hover:bg-[#374151]" title={!balanced ? "Total Debit must equal Total Credit to save" : undefined}>
              <Send className="mr-2 h-4 w-4" /> Save {shortTitle}
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" disabled={busy} onClick={saveDraft}><Save className="mr-2 h-4 w-4" /> Save Draft</Button>
              <Button type="button" disabled={busy || !balanced} onClick={saveAndPost} className="bg-[#111827] text-white hover:bg-[#374151]" title={!balanced ? "Total Debit must equal Total Credit to post" : undefined}>
                <Send className="mr-2 h-4 w-4" /> Save &amp; Post
              </Button>
            </>
          )}
          {!balanced && lines.length > 0 && <span className="self-center text-xs text-[#6B7280]">Difference: {formatCurrency(Math.abs(totalDebit - totalCredit))} - {immediateSave ? "Save" : "Post"} is disabled until Total Debit = Total Credit.</span>}
        </div>
        <p className="mt-2 text-xs text-[#6B7280]">
          {immediateSave
            ? `Saving this ${shortTitle} creates the accounting transaction immediately and updates the General Ledger and Trial Balance right away - it is not a draft.`
            : "Draft is not posted to the books. Save Draft first if you want to review before posting, or edit it later from All Vouchers."}
        </p>
      </SectionCard>

      <SectionCard title={`Recent ${shortTitle} Vouchers`}>
        <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Voucher No.</TableHead><TableHead>Reference</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
              ) : vouchers.slice(0, 20).map((v) => (
                <TableRow key={v.id}>
                  <TableCell>{v.voucherDate}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {v.voucherNumber || <span className="rounded-sm bg-[#F3F4F6] px-1.5 py-0.5 font-sans text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">Draft</span>}
                  </TableCell>
                  <TableCell>{v.referenceNo || "-"}</TableCell>
                  <TableCell className="capitalize">{v.status}</TableCell>
                  <TableCell className="text-right">{formatCurrency(v.totalDebit)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-2 text-xs font-semibold">
                      <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}`} className="text-[#111827] hover:underline">View</Link>
                      {v.status === "draft" && (
                        <>
                          <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}/edit`} className="text-[#111827] hover:underline">Edit</Link>
                          <button type="button" onClick={() => postExistingDraft(v)} className="text-[#111827] hover:underline">Post</button>
                          <button type="button" onClick={() => deleteExistingDraft(v)} className="text-red-600 hover:underline">Delete</button>
                        </>
                      )}
                      {v.status === "posted" && (
                        immediateSave ? (
                          // Phase 13 (Journal/Contra correction), item 3: Journal has no
                          // more Draft/Post/Cancel lifecycle at all - every posted Journal
                          // simply gets Edit/Delete, dependency-checked by the backend.
                          <>
                            <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}/edit`} className="text-[#111827] hover:underline">Edit</Link>
                            <button type="button" onClick={() => deletePostedVoucher(v)} className="text-red-600 hover:underline">Delete</button>
                          </>
                        ) : voucherType === "PURCHASE" ? (
                          // Phase 15 (Vendor Bills & Payables) item 9: "View | Edit | Print
                          // | Delete. Show only safe actions" - the generic engine already
                          // supports Edit/Delete for any posted voucher (dependency-checked
                          // via _engine_voucher_downstream_dependencies, including the
                          // Phase 15 payment-allocation check), this just surfaces them here
                          // too instead of only on the full Voucher View page. Cancel/
                          // Reverse remains available too (also now dependency-checked, see
                          // engine_cancel_voucher) for the audited-reversal case.
                          <>
                            <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}/edit`} className="text-[#111827] hover:underline">Edit</Link>
                            <Link to={`${ACCOUNTING_BASE}/vouchers/${v.id}?print=1`} className="text-[#111827] hover:underline">Print</Link>
                            <button type="button" onClick={() => deletePostedVoucher(v)} className="text-red-600 hover:underline">Delete</button>
                            <button type="button" onClick={() => cancelPostedVoucher(v)} className="text-red-600 hover:underline">Cancel</button>
                          </>
                        ) : (
                          // Credit Note/Debit Note are out of scope for this phase and keep
                          // their original Cancel-only behavior.
                          <button type="button" onClick={() => cancelPostedVoucher(v)} className="text-red-600 hover:underline">Cancel</button>
                        )
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && vouchers.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-[#6B7280]">{emptyStateNote || `No ${shortTitle.toLowerCase()} vouchers yet. Create your first one above.`}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </SectionCard>
    </div>
  );
}
